import { API_BASE_URL } from '../api';
import { readConsent } from './consent';

let active = null;
const remembered = new Set();
const inFlight = new Map();
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function rememberDispatched(key) {
  try {
    const storage = window.localStorage;
    storage.setItem(key, String(Date.now()));
    const records = [];
    for (let index = 0; index < storage.length; index += 1) {
      const candidate = storage.key(index);
      if (candidate?.startsWith('mib_conversion:')) records.push([candidate, Number(storage.getItem(candidate))]);
    }
    records.sort((a, b) => b[1] - a[1]).forEach(([candidate, time], index) => {
      if (index >= 500 || !time || Date.now() - time > 180 * 86400000) storage.removeItem(candidate);
    });
  } catch { /* Consent permits this storage, but browser policy can still block it. */ }
}

export async function fetchTrackingConfig(orgSlug) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 2500);
  try {
    const response = await fetch(`${API_BASE_URL}/api/public/${encodeURIComponent(orgSlug)}/tracking-config`, { credentials: 'omit', signal: controller.signal });
    if (!response.ok) throw new Error('Tracking-Einstellungen sind nicht erreichbar.');
    return await response.json();
  } finally { window.clearTimeout(timer); }
}

export function safeTrackingEvent(event) {
  if (!['lead', 'booking'].includes(event?.name) || !UUID.test(event?.event_id || '')) return null;
  return { name: event.name, event_id: event.event_id };
}

export function allowedServices(config, consent) {
  return {
    ga4: config.ga4_enabled === true && consent?.analytics === true,
    google_ads: config.google_ads_enabled === true && consent?.marketing === true,
    meta: config.meta_enabled === true && consent?.marketing === true,
  };
}

// A disposable document keeps vendor listeners out of forms, medical data and SPA navigation.
// Destroying it also stops future tag activity when consent is revoked.
export function mountTracking(config, orgSlug) {
  const consent = readConsent(config);
  if (active?.orgSlug === orgSlug && active.config.revision === config.revision
    && active.consentAt === consent?.saved_at) return active.dispose;
  active?.dispose();
  const services = allowedServices(config, consent);
  // readConsent accepts only the current, verified website connection in an iframe.
  if (!Object.values(services).some(Boolean)) return () => {};
  const frame = document.createElement('iframe');
  frame.title = 'Einwilligungsbasierte Erfolgsmessung';
  frame.hidden = true;
  frame.setAttribute('aria-hidden', 'true');
  frame.referrerPolicy = 'no-referrer';
  frame.src = '/tracking-runtime.html';
  const pending = new Map();
  const queue = [];
  let ready = false;
  let disposed = false;
  const channel = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  const publicConfig = {
    ga4_measurement_id: services.ga4 ? config.ga4_measurement_id : null,
    google_ads_conversion_id: services.google_ads ? config.google_ads_conversion_id : null,
    google_ads_label_booking: services.google_ads ? config.google_ads_label_booking : null,
    google_ads_label_lead: services.google_ads ? config.google_ads_label_lead : null,
    meta_pixel_id: services.meta ? config.meta_pixel_id : null,
    cookie_prefix: `mib_${String(config.organization_id).replace(/[^a-z0-9]/gi, '')}`,
    analytics: services.ga4, marketing: services.google_ads || services.meta,
  };
  // Only click identifiers, never query strings, treatment names, referrers or answers.
  const clicks = {};
  if (consent.marketing) {
    const query = new URLSearchParams(window.location.search);
    for (const key of ['gclid', 'gbraid', 'wbraid', 'fbclid']) {
      const value = query.get(key);
      if (value && /^[a-zA-Z0-9_.~-]{1,500}$/.test(value)) clicks[key] = value;
    }
  }
  const post = (data) => frame.contentWindow?.postMessage({ ...data, channel }, window.location.origin);
  const receive = (event) => {
    if (event.source !== frame.contentWindow || event.origin !== window.location.origin) return;
    if (event.data?.type === 'mib:tracking-ready') {
      post({ type: 'mib:tracking-init', config: publicConfig, clicks });
      ready = true;
      queue.splice(0).forEach(post);
    } else if (event.data?.type === 'mib:tracking-dispatched' && event.data.channel === channel) {
      pending.get(event.data.event_id)?.(event.data.services || []);
      pending.delete(event.data.event_id);
    }
  };
  window.addEventListener('message', receive);
  const instance = {
    config, orgSlug, services, consentAt: consent?.saved_at,
    send(event, destinations) {
      if (disposed) return Promise.resolve([]);
      return new Promise((resolve) => {
        // Keep listening for late SDK completion. Only the success UI has a time
        // budget; forgetting this acknowledgement would make retries double count.
        pending.set(event.event_id, resolve);
        const message = { type: 'mib:tracking-event', event, destinations };
        if (ready) post(message); else queue.push(message);
      });
    },
    dispose() {
      disposed = true;
      window.removeEventListener('message', receive);
      frame.remove();
      pending.forEach((finish) => finish([]));
      pending.clear();
      if (active === instance) active = null;
    },
  };
  active = instance;
  document.body.appendChild(frame);
  return instance.dispose;
}

async function dispatchTrackingEvent(metadata) {
  try {
    const event = safeTrackingEvent(metadata);
    if (!event || typeof metadata?.org_slug !== 'string') return;
    // Payment finalization can finish before the page's practice data has loaded.
    // Only an already stored, valid consent may authorize that event.
    if (!active) {
      const config = await fetchTrackingConfig(metadata.org_slug);
      if (!readConsent(config)) return;
      if (!active) mountTracking(config, metadata.org_slug);
    }
    const instance = active;
    if (!event || !instance || metadata.org_slug !== instance.orgSlug) return;
    const current = allowedServices(instance.config, readConsent(instance.config));
    const keys = {};
    const destinations = Object.keys(current).filter((service) => {
      if (!current[service]) return false;
      const config = instance.config;
      const destination = service === 'ga4' ? config.ga4_measurement_id
        : service === 'meta' ? config.meta_pixel_id
          : `${config.google_ads_conversion_id}/${config[`google_ads_label_${event.name}`] || ''}`;
      const key = `mib_conversion:${config.organization_id}:${service}:${destination}:${event.name}:${event.event_id}`;
      keys[service] = key;
      try {
        const savedAt = Number(window.localStorage.getItem(key));
        if (savedAt && Date.now() - savedAt < 180 * 86400000) return false;
      } catch { /* Memory dedup remains available. */ }
      if (remembered.has(key)) return false;
      remembered.add(key); // Also excludes concurrent callbacks for the same response.
      return true;
    });
    if (!destinations.length) return;
    const sent = await instance.send(event, destinations);
    destinations.forEach((service) => {
      if (sent.includes(service)) {
        rememberDispatched(keys[service]);
      } else remembered.delete(keys[service]);
    });
  } catch { /* Tracking never interrupts a booking or inquiry. */ }
}

export function emitTrackingEvent(metadata) {
  const event = safeTrackingEvent(metadata);
  if (!event || typeof metadata?.org_slug !== 'string') return Promise.resolve();
  const key = JSON.stringify([metadata.org_slug, event.name, event.event_id]);
  // Duplicate completion callbacks must share the original wait. Returning early
  // for a pending event would let the second caller destroy its measurement frame.
  if (inFlight.has(key)) return inFlight.get(key);
  // Success UI / redirects wait at most this budget, including configuration fetches.
  const result = new Promise((resolve) => {
    // GA4 can batch a custom event for about five seconds. Its neutral document
    // must survive until transport acknowledgement, with a bounded UI fallback.
    const timer = window.setTimeout(resolve, 6500);
    const work = navigator.locks?.request && safeTrackingEvent(metadata)
      ? navigator.locks.request(`mib_tracking:${metadata.org_slug}:${metadata.event_id}`, () => dispatchTrackingEvent(metadata))
      : dispatchTrackingEvent(metadata);
    Promise.resolve(work).catch(() => {}).finally(() => { window.clearTimeout(timer); resolve(); });
  });
  inFlight.set(key, result);
  result.then(() => inFlight.delete(key), () => inFlight.delete(key));
  return result;
}
